import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useQuery } from 'react-query';
import { shipmentsAPI, usersAPI, routesAPI, citiesAPI } from '../services/api';
import { Package, Search, Filter, Plus, Download, Eye, Edit, ChevronLeft, ChevronRight } from 'lucide-react';
import LoadingSpinner from '../components/LoadingSpinner';
import { exportShipmentsToCSV, exportShipmentsToExcel } from '../utils/exportUtils';
import Datepicker from "react-tailwindcss-datepicker";

const Shipments = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [assigneeFilter, setAssigneeFilter] = useState('');
  const [routeFilter, setRouteFilter] = useState('');
  const [cityFilter, setCityFilter] = useState('');
  const [creatorFilter, setCreatorFilter] = useState('');
  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(20);
  const [totalPages, setTotalPages] = useState(1);
  const [dueDateRange, setDueDateRange] = useState({ startDate: null, endDate: null });
  const [deliveryDateRange, setDeliveryDateRange] = useState({ startDate: null, endDate: null });

  // Read status parameter from URL on component mount
  useEffect(() => {
    const urlParams = new URLSearchParams(location.search);
    const statusParam = urlParams.get('status');
    if (statusParam) {
      setStatusFilter(statusParam);
    }
  }, [location.search]);

  // Build params for API
  const apiParams = {
    page,
    limit,
    status: statusFilter || undefined,
    assigneeId: assigneeFilter ? Number(assigneeFilter) : undefined,
    routeId: routeFilter ? Number(routeFilter) : undefined,
    cityId: cityFilter ? Number(cityFilter) : undefined,
    createdUserId: creatorFilter ? Number(creatorFilter) : undefined,
    search: searchTerm || undefined,
    dueDateFrom: dueDateRange.startDate
      ? new Date(dueDateRange.startDate).toISOString().split('T')[0]
      : undefined,
    dueDateTo: dueDateRange.endDate
      ? new Date(dueDateRange.endDate).toISOString().split('T')[0]
      : undefined,
    deliveryDateFrom: deliveryDateRange.startDate
      ? new Date(deliveryDateRange.startDate).toISOString().split('T')[0]
      : undefined,
    deliveryDateTo: deliveryDateRange.endDate
      ? new Date(deliveryDateRange.endDate).toISOString().split('T')[0]
      : undefined,
  };

  const { data: shipmentsData, isLoading, error, refetch } = useQuery(
    ['shipments', apiParams],
    () => shipmentsAPI.getAll(apiParams),
    {
      keepPreviousData: true,
      refetchInterval: 30000, // Refetch every 30 seconds
    }
  );

  const { data: usersData } = useQuery('activeUsers', () => usersAPI.getActive());
  const { data: routesData } = useQuery('routes', () => routesAPI.getAll());
  const { data: citiesData } = useQuery('cities', () => citiesAPI.getAll());

  useEffect(() => {
    if (shipmentsData?.pagination) {
      setTotalPages(shipmentsData.pagination.totalPages);
    }
  }, [shipmentsData]);

  useEffect(() => {
    setPage(1);
  }, [searchTerm, statusFilter, assigneeFilter, routeFilter, cityFilter, creatorFilter, dueDateRange, deliveryDateRange]);

  // Handler to ensure correct format for datepicker value
  const handleDueDateRangeChange = (newValue) => {
    setDueDateRange({
      startDate: newValue?.startDate ? String(newValue.startDate) : null,
      endDate: newValue?.endDate ? String(newValue.endDate) : null,
    });
  };

  const handleDeliveryDateRangeChange = (newValue) => {
    setDeliveryDateRange({
      startDate: newValue?.startDate ? String(newValue.startDate) : null,
      endDate: newValue?.endDate ? String(newValue.endDate) : null,
    });
  };

  if (isLoading) return <LoadingSpinner />;
  if (error) return <div className="text-red-600">Error loading shipments</div>;

  // Extract shipments array from the API response
  const shipments = shipmentsData?.shipments || [];
  const users = usersData?.users || [];
  const routes = routesData?.routes || [];
  const cities = citiesData?.cities || [];

  const getStatusColor = (status) => {
    switch (status) {
      case 'completed':
        return 'bg-green-100 text-green-800';
      case 'in_progress':
        return 'bg-blue-100 text-blue-800';
      case 'pending':
        return 'bg-yellow-100 text-yellow-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  const getShipmentStatusColor = (status) => {
    switch (status) {
      case 'delivered':
        return 'bg-green-100 text-green-800';
      case 'in_transit':
        return 'bg-blue-100 text-blue-800';
      case 'pending':
        return 'bg-yellow-100 text-yellow-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  const handleShipmentClick = (shipmentId) => {
    navigate(`/shipments/${shipmentId}`);
  };

  const handleExport = async (format) => {
    try {
      // Fetch all matching shipments for export
      const exportParams = {
        ...apiParams,
        export: true,
        limit: 10000,
        page: 1,
      };
      const exportData = await shipmentsAPI.getAll(exportParams);
      const allShipments = exportData?.shipments || [];
      
      if (format === 'csv') {
        exportShipmentsToCSV(allShipments);
      } else {
        exportShipmentsToExcel(allShipments);
      }
    } catch (error) {
      console.error('Export error:', error);
      // You could add a toast notification here if available
    }
  };

  return (
    <div className="space-y-3 lg:space-y-4 xl:space-y-6">
      {isLoading ? (
        <LoadingSpinner />
      ) : error ? (
        <div className="text-red-600">Error loading shipments</div>
      ) : (
        <>
          {/* Header */}
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2 lg:gap-4">
            <div>
              <h1 className="text-xl lg:text-2xl xl:text-3xl font-bold text-gray-900">Shipments</h1>
              <p className="text-sm lg:text-base text-gray-600">Manage and track all shipments</p>
            </div>
            <button 
              className="flex items-center justify-center px-3 lg:px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors text-sm"
              onClick={() => navigate('/shipments/new')}
            >
              <Plus className="h-4 w-4 mr-2" />
              <span className="hidden sm:inline">New Shipment</span>
              <span className="sm:hidden">New</span>
            </button>
          </div>

          {/* Filters and Search */}
          <div className="bg-white rounded-lg shadow p-3 lg:p-4 xl:p-6">
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-6 gap-2 lg:gap-3 xl:gap-4">
              <div className="sm:col-span-2 lg:col-span-2">
                <div className="relative">
                  <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 h-4 w-4" />
                  <input
                    type="text"
                    placeholder="Search shipments..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-sm"
                  />
                </div>
              </div>
              <div>
                <select
                  value={statusFilter}
                  onChange={(e) => setStatusFilter(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-sm"
                >
                  <option value="">All Statuses</option>
                  <option value="inquiry">Inquiry</option>
                  <option value="box_collection">Box Collection</option>
                  <option value="box_collection_done">Box Collection Done</option>
                  <option value="box_delivery">Box Delivery</option>
                  <option value="box_delivery_done">Box Delivery Done</option>
                  <option value="quick_packing">Quick Packing</option>
                  <option value="quick_packing_done">Quick Packing Done</option>
                  <option value="shipment">Ready for Shipment</option>
                </select>
              </div>
              <div>
                <select
                  value={assigneeFilter}
                  onChange={(e) => setAssigneeFilter(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-sm"
                >
                  <option value="">All Assignees</option>
                  {users.map(user => (
                    <option key={user.id} value={user.id}>{user.full_name}</option>
                  ))}
                </select>
              </div>
              <div>
                <select
                  value={routeFilter}
                  onChange={(e) => setRouteFilter(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-sm"
                >
                  <option value="">All Routes</option>
                  {routes.map(route => (
                    <option key={route.id} value={route.id}>{route.name}</option>
                  ))}
                </select>
              </div>
              <div>
                <select
                  value={cityFilter}
                  onChange={(e) => setCityFilter(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-sm"
                >
                  <option value="">All Cities</option>
                  {cities.map(city => (
                    <option key={city.id} value={city.id}>{city.name}</option>
                  ))}
                </select>
              </div>
              <div>
                <select
                  value={creatorFilter}
                  onChange={(e) => setCreatorFilter(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-sm"
                >
                  <option value="">All Creators</option>
                  {users.map(user => (
                    <option key={user.id} value={user.id}>{user.full_name}</option>
                  ))}
                </select>
              </div>
              <div>
                <Datepicker
                  value={dueDateRange}
                  onChange={handleDueDateRangeChange}
                  displayFormat="MM/DD/YYYY"
                  inputClassName="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-sm"
                  placeholder="Select Due Date Range"
                  popoverDirection="down"
                />
              </div>
              <div>
                <Datepicker
                  value={deliveryDateRange}
                  onChange={handleDeliveryDateRangeChange}
                  displayFormat="MM/DD/YYYY"
                  inputClassName="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-sm"
                  placeholder="Select Delivery Date Range"
                  popoverDirection="down"
                />
              </div>
            </div>
          </div>

          {/* Shipments List */}
          <div className="bg-white rounded-lg shadow overflow-hidden">
            {shipments.length > 0 ? (
              <>
                {/* Desktop Table */}
                <div className="hidden lg:block overflow-x-auto">
                  <table className="min-w-full divide-y divide-gray-200">
                    <thead className="bg-gray-50">
                      <tr>
                        <th className="px-4 lg:px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Shipment ID
                        </th>
                        <th className="px-4 lg:px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Customer
                        </th>
                        <th className="px-4 lg:px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          City
                        </th>
                        <th className="px-4 lg:px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Route
                        </th>
                        <th className="px-4 lg:px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Assigned User
                        </th>
                        <th className="px-4 lg:px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Created By
                        </th>
                        <th className="px-4 lg:px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Status
                        </th>
                        <th className="px-4 lg:px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Shipment Status
                        </th>
                        <th className="px-4 lg:px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Delivery Date
                        </th>
                        <th className="px-4 lg:px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Due Date
                        </th>
                        <th className="px-4 lg:px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Created Date
                        </th>
                        <th className="px-4 lg:px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Actions
                        </th>
                      </tr>
                    </thead>
                    <tbody className="bg-white divide-y divide-gray-200">
                      {shipments.map((shipment) => (
                        <tr 
                          key={shipment.id} 
                          className="hover:bg-gray-50 cursor-pointer"
                          onClick={() => handleShipmentClick(shipment.id)}
                        >
                          <td className="px-4 lg:px-6 py-3 lg:py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                            #{shipment.id}
                          </td>
                          <td className="px-4 lg:px-6 py-3 lg:py-4 whitespace-nowrap">
                            <div>
                              <div className="text-sm font-medium text-gray-900">{shipment.customer_name}</div>
                              <div className="text-sm text-gray-500">{shipment.customer_phone}</div>
                            </div>
                          </td>
                          <td className="px-4 lg:px-6 py-3 lg:py-4 whitespace-nowrap text-sm text-gray-900">
                            {shipment.city_name}
                          </td>
                          <td className="px-4 lg:px-6 py-3 lg:py-4 whitespace-nowrap text-sm text-gray-900">
                            {shipment.route_name || 'Not assigned'}
                          </td>
                          <td className="px-4 lg:px-6 py-3 lg:py-4 whitespace-nowrap text-sm text-gray-900">
                            {shipment.assigned_user_name || 'Not assigned'}
                          </td>
                          <td className="px-4 lg:px-6 py-3 lg:py-4 whitespace-nowrap text-sm text-gray-900">
                            {shipment.created_by_name || 'Unknown'}
                          </td>
                          <td className="px-4 lg:px-6 py-3 lg:py-4 whitespace-nowrap">
                            <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${getStatusColor(shipment.status)}`}>
                              {shipment.status.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase())}
                            </span>
                          </td>
                          <td className="px-4 lg:px-6 py-3 lg:py-4 whitespace-nowrap">
                            {shipment.shipment_status && (
                              <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${getShipmentStatusColor(shipment.shipment_status)}`}>
                                {shipment.shipment_status.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase())}
                              </span>
                            )}
                          </td>
                          <td className="px-4 lg:px-6 py-3 lg:py-4 whitespace-nowrap text-sm text-gray-900">
                            {shipment.delivery_date ? new Date(shipment.delivery_date).toLocaleDateString() : 'Not set'}
                          </td>
                          <td className="px-4 lg:px-6 py-3 lg:py-4 whitespace-nowrap text-sm text-gray-900">
                            {shipment.due_date ? new Date(shipment.due_date).toLocaleDateString() : 'Not set'}
                          </td>
                          <td className="px-4 lg:px-6 py-3 lg:py-4 whitespace-nowrap text-sm text-gray-900">
                            {shipment.created_at ? new Date(shipment.created_at).toLocaleDateString() : 'Unknown'}
                          </td>
                          <td className="px-4 lg:px-6 py-3 lg:py-4 whitespace-nowrap text-sm font-medium">
                            <button 
                              className="text-blue-600 hover:text-blue-900 mr-3"
                              onClick={(e) => {
                                e.stopPropagation();
                                handleShipmentClick(shipment.id);
                              }}
                            >
                              View
                            </button>
                            <button 
                              className="text-gray-600 hover:text-gray-900"
                              onClick={(e) => {
                                e.stopPropagation();
                                navigate(`/shipments/${shipment.id}/edit`);
                              }}
                            >
                              Edit
                            </button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>

                {/* Mobile Cards */}
                <div className="lg:hidden">
                  <div className="divide-y divide-gray-200">
                    {shipments.map((shipment) => (
                      <div 
                        key={shipment.id} 
                        className="p-3 lg:p-4 hover:bg-gray-50 cursor-pointer transition-colors"
                        onClick={() => handleShipmentClick(shipment.id)}
                      >
                        <div className="flex items-start justify-between mb-2 lg:mb-3">
                          <div className="flex-1 min-w-0">
                            <h3 className="text-sm font-medium text-gray-900 truncate">
                              Shipment #{shipment.id}
                            </h3>
                            <p className="text-sm text-gray-600 truncate">{shipment.customer_name}</p>
                            <p className="text-xs text-gray-500">{shipment.customer_phone}</p>
                          </div>
                          <div className="flex items-center space-x-1 lg:space-x-2 ml-2">
                            <button 
                              className="p-1 text-blue-600 hover:text-blue-900"
                              onClick={(e) => {
                                e.stopPropagation();
                                handleShipmentClick(shipment.id);
                              }}
                            >
                              <Eye className="h-4 w-4" />
                            </button>
                            <button 
                              className="p-1 text-gray-600 hover:text-gray-900"
                              onClick={(e) => {
                                e.stopPropagation();
                                navigate(`/shipments/${shipment.id}/edit`);
                              }}
                            >
                              <Edit className="h-4 w-4" />
                            </button>
                          </div>
                        </div>
                        
                        <div className="grid grid-cols-2 gap-2 lg:gap-3 text-xs lg:text-sm">
                          <div>
                            <span className="text-gray-500">City:</span>
                            <span className="ml-1 text-gray-900">{shipment.city_name}</span>
                          </div>
                          <div>
                            <span className="text-gray-500">Route:</span>
                            <span className="ml-1 text-gray-900">{shipment.route_name || 'Not assigned'}</span>
                          </div>
                          <div>
                            <span className="text-gray-500">Assigned:</span>
                            <span className="ml-1 text-gray-900">{shipment.assigned_user_name || 'Not assigned'}</span>
                          </div>
                          <div>
                            <span className="text-gray-500">Delivery:</span>
                            <span className="ml-1 text-gray-900">
                              {shipment.delivery_date ? new Date(shipment.delivery_date).toLocaleDateString() : 'Not set'}
                            </span>
                          </div>
                          <div>
                            <span className="text-gray-500">Due:</span>
                            <span className="ml-1 text-gray-900">
                              {shipment.due_date ? new Date(shipment.due_date).toLocaleDateString() : 'Not set'}
                            </span>
                          </div>
                        </div>
                        
                        <div className="flex items-center justify-between mt-2 lg:mt-3">
                          <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${getStatusColor(shipment.status)}`}>
                            {shipment.status.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase())}
                          </span>
                          {shipment.shipment_status && (
                            <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${getShipmentStatusColor(shipment.shipment_status)}`}>
                              {shipment.shipment_status.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase())}
                            </span>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>

                {/* Export Options */}
                <div className="flex justify-end space-x-2 lg:space-x-3">
                  <button
                    onClick={() => handleExport('csv')}
                    className="px-3 lg:px-4 py-2 text-sm border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50 transition-colors flex items-center"
                  >
                    <Download className="h-4 w-4 mr-1" />
                    Export CSV
                  </button>
                  <button
                    onClick={() => handleExport('excel')}
                    className="px-3 lg:px-4 py-2 text-sm border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50 transition-colors flex items-center"
                  >
                    <Download className="h-4 w-4 mr-1" />
                    Export Excel
                  </button>
                </div>

                {/* Pagination Controls */}
                {totalPages > 1 && (
                  <div className="flex justify-between items-center px-4 py-3 border-t border-gray-200 bg-gray-50">
                    <div className="text-sm text-gray-600">
                      Showing {(page - 1) * limit + 1} - {Math.min(page * limit, shipmentsData?.pagination?.total || 0)} of {shipmentsData?.pagination?.total || 0} shipments
                    </div>
                    <div className="flex items-center gap-2">
                      <button
                        className="p-2 rounded-full border border-gray-300 disabled:opacity-50"
                        onClick={() => setPage(page - 1)}
                        disabled={page === 1}
                      >
                        <ChevronLeft className="h-4 w-4" />
                      </button>
                      <span className="text-sm font-medium">Page {page} of {totalPages}</span>
                      <button
                        className="p-2 rounded-full border border-gray-300 disabled:opacity-50"
                        onClick={() => setPage(page + 1)}
                        disabled={page === totalPages}
                      >
                        <ChevronRight className="h-4 w-4" />
                      </button>
                    </div>
                  </div>
                )}
              </>
            ) : (
              <div className="text-center py-8 lg:py-12">
                <Package className="h-12 w-12 lg:h-16 lg:w-16 text-gray-400 mx-auto mb-4" />
                <h3 className="text-lg lg:text-xl font-medium text-gray-900 mb-2">No shipments found</h3>
                <p className="text-sm lg:text-base text-gray-600 mb-4">
                  {searchTerm || statusFilter || assigneeFilter || routeFilter 
                    ? 'Try adjusting your filters or search terms'
                    : 'Get started by creating your first shipment'
                  }
                </p>
                <button
                  onClick={() => navigate('/shipments/new')}
                  className="inline-flex items-center px-4 py-2 border border-transparent shadow-sm text-sm font-medium rounded-md text-white bg-blue-600 hover:bg-blue-700"
                >
                  <Plus className="h-4 w-4 mr-2" />
                  Create Shipment
                </button>
              </div>
            )}
          </div>
        </>
      )}
    </div>
  );
}

export default Shipments; 